/**
 * @file api-site-templates.ts
 * @description Клиент шаблона страницы товара и переноса элементов между
 * блоками (phase19.16 Р2а «Каталог и товар»; db-design-r2a, ответы 2, 2а, 4;
 * uiux-design-r2a §3; контракт site-view-api-contract.md §19 «Каталог и
 * шаблон товара»).
 *
 * Шаблон — служебная страница `kind = TEMPLATE` (адрес `_template-product`),
 * её блоки правятся обычными эндпоинтами блоков (§16.2) и попадают в
 * черновик части PRODUCT_TEMPLATE; на сайт — вместе с видом сайта.
 * Ответы `{ data }` / `{ error }` разбирает `apiFetch` (ошибка — `ApiError`).
 */

import type { ProductTemplateLayoutKey, TemplateIssue } from "@chaiberi/shared";

import { apiFetch } from "./api-client";
import type { SitePageBlock, SitePageDetail } from "./api-site-pages";
import type { SitePartState } from "./site-view/types";

/** Товар для примера на канве шаблона (§19.3 `sample`). */
export interface TemplateSampleProduct {
  productId: string;
  slug: string;
  title: string;
}

/** Строка «Товар для примера ▾» (§19.3 `sample-products`). */
export interface TemplateSampleItem extends TemplateSampleProduct {
  imageUrl: string | null;
  skuCount: number;
  purchasable: boolean;
  reviewsCount: number;
  hasDescription: boolean;
}

/** `ProductTemplateDto` (§19.3). */
export interface ProductTemplateResponse {
  /** Страница-шаблон с блоками (у блоков — `dataCounts`, лимит 20). */
  page: SitePageDetail;
  /** Замечания `checkProductTemplate` по черновику: error — публикация будет заблокирована. */
  issues: TemplateIssue[];
  /** Сколько товаров на сайте получат шаблон (SB38). */
  appliesTo: number;
  /** Последний изменённый видимый товар; `null` — товаров на сайте нет. */
  sample: TemplateSampleProduct | null;
  /** Часть PRODUCT_TEMPLATE: версия, кто правил, изменена ли относительно сайта. */
  part: SitePartState;
}

/** Быстрые фильтры «Товар для примера ▾» (uiux-design-r2a §3). */
export const SAMPLE_FILTERS = ["multi-sku", "no-photo", "coming-soon", "no-reviews", "no-description"] as const;
export type SampleFilter = (typeof SAMPLE_FILTERS)[number];

/** Подписи быстрых фильтров. */
export const SAMPLE_FILTER_LABELS: Readonly<Record<SampleFilter, string>> = {
  "multi-sku": "С несколькими фасовками",
  "no-photo": "Без фото",
  "coming-soon": "Скоро в продаже",
  "no-reviews": "Без отзывов",
  "no-description": "Без описания",
};

/** Тело переноса элемента (`POST /admin/site/blocks/:id/elements/move`, §19.3). */
export interface MoveElementInput {
  elementId: string;
  /** Блок той же страницы (можно тот же). */
  toBlockId: string;
  /** Колонка блока-получателя (с 0). */
  toColumn: number;
  /** Место в колонке; больше длины — в конец. */
  toIndex: number;
  /** `updatedAt` блоков, которые видел кабинет (409 `block_changed`). */
  expectedUpdatedAt?: string;
  toExpectedUpdatedAt?: string;
}

/** Ответ переноса: изменённые блоки (1 или 2). */
export interface MoveElementResult {
  blocks: SitePageBlock[];
}

/** «В конец колонки» для `toIndex`. */
export const TO_END = 10_000;

const BASE = "/admin/site/templates/product";

export const siteTemplates = {
  /** Шаблон страницы товара; страница создаётся сервером при первом обращении. */
  product: () => apiFetch<ProductTemplateResponse>(BASE),
  /**
   * «Сбросить к стандартному» или применить раскладку (`layout`): блоки
   * черновика шаблона заменяются целиком; на сайте прежний — до публикации.
   */
  reset: (layout?: ProductTemplateLayoutKey) =>
    apiFetch<ProductTemplateResponse>(`${BASE}/reset`, { method: "POST", json: layout ? { layout } : {} }),
  /** Товары для примера: поиск и быстрый фильтр (только видимые на сайте). */
  sampleProducts: (query: { q?: string; filter?: SampleFilter | null; limit?: number }) => {
    const q = new URLSearchParams();
    if (query.q?.trim()) q.set("q", query.q.trim());
    if (query.filter) q.set("filter", query.filter);
    if (query.limit) q.set("limit", String(query.limit));
    const qs = q.toString();
    return apiFetch<TemplateSampleItem[]>(`${BASE}/sample-products${qs ? `?${qs}` : ""}`);
  },
};

/** Перенос элемента секции в другой блок той же страницы (любой страницы, не только шаблона). */
export function moveElementToBlock(block: Pick<SitePageBlock, "id">, input: MoveElementInput): Promise<MoveElementResult> {
  return apiFetch<MoveElementResult>(`/admin/site/blocks/${block.id}/elements/move`, { method: "POST", json: input });
}
